import {
  getCollectionsById,
  getProductsByEventName,
  getShopLocationCodeList,
  createProduct,
  deleteProduct,
  editProduct,
  getCollectionsByEventName,
  createCollection,
  deleteCollection,
  editCollection,
  reorderCollections,
  getEventOrders,
  fulfillOrder,
  printOrderPhotos,
  getCollectionsWithProductsByEventName,
  createDraftOrder,
  getAttendeeLastOrder,
  completeDraftOrder,
  completeShopifyDraftOrderSelfCheckout,
  addStoreSettings,
  editStoreSettings,
  getStoreSettings,
  deleteStoreSettings,
  addStoreLocation,
  editStoreLocation,
  getStoreLocation,
  deleteStoreLocation,
  getStoreSettingsForKiosk,
  getStoreLocationForKiosk,
} from './EcommerceService.js';
import {
  ecomGetComboProductsIds,
  ecomGetCollComboVariant,
  ecomGetCollectionPackageVariantID,
  ecomGetProductsFromCollection,
  ecomGetProductCountInCollection,
  orderProductsByCollectionJson,
  groupVariantsByIdInOrder,
  normalizeProductId as normalizeProductIdInternal,
  normalizeCollectionId as normalizeCollectionIdInternal,
  ecomInternalProductToVariant,
  ecomExtractPageLang,
  ecomGetLocalisedObjectImage,
  ecomFormatCurrency,
  ecomFormatDecimals,
  ecomFormatPrice,
  ecomIsNodeTitleHasCorrectMode,
} from './modules/collections.js';
import {
  ecomGetProductTagType,
  ecomCheckProductTagTypeAndImageTag,
  ecomCheckProductTagTypeForAllImages,
  fromUrlFilename,
} from './modules/tags.js';
import {
  ecomGetCart as cartGetCart,
  ecomAddLineItems as cartAddLineItems,
  ecomUpdateCartLineItems as cartUpdateCartLineItems,
  ecomApplyDiscount as cartApplyDiscount,
} from './modules/cart.js';
import { isFalsy,tryJson, escapeHtml } from '../utils/commonUtil.js';
import { t } from '../localization/index.js';
import {
  loadEcommerceCheckoutSettings,
  applyEcommerceCheckoutSettingsToStorage,
  extractEcomApiRecord,
  mapLocationToLegacyShippingAddress,
  isTruthyEcomFlag,
  toEcomStorageOneOrZero,
  parseLegacyShippingAddress,
} from './modules/checkoutSettings.js';
import { normalizeEventOrder, normalizeEventOrders } from './modules/eventOrders.js';
// Keep existing public API surface; delegate implementation to modules where possible

export function ecomIsCollectionHiddenFromPackageList(checkingObj) {
  const visibilityJson = tryJson(checkingObj?.visibility_json, {});
  return (
    visibilityJson.hide_from_packages === true ||
    visibilityJson.hide_from_packages === 1 ||
    checkingObj?.hide_from_packages === true ||
    checkingObj?.hide_from_packages === 1
  );
}

function isTipCollectionFlag(value) {
  return value === true || value === 1 || value === '1';
}

export function ecomIsCollectionVisibleInPackageList(checkingObj) {
  // Tip collections are checkout-only and must not appear in package grids.
  if (isTipCollectionFlag(checkingObj?.is_tip_collection)) {
    return false;
  }
  return !ecomIsCollectionHiddenFromPackageList(checkingObj);
}

export function ecomGetCollectionOrProductVisibility(checkingObj, isVip) {
  const currKioskId = localStorage.getItem('kioskId')?.toLowerCase();
  const toHideKiosks = checkingObj.hide_from_kiosks || checkingObj.hideFromKiosks;
  const toShowOnlyKiosks = checkingObj.show_only_on_kiosks || checkingObj.showOnlyOnKiosks;
  const showToWip = checkingObj?.show_only_for_vip || checkingObj?.showOnlyForVip;
  const hideForWip = checkingObj?.hide_for_vip || checkingObj?.hideForVip;
  const visibilityJson = tryJson(checkingObj.visibility_json, {});
  if (visibilityJson?.kiosk === false) {
    return false;
  }
  const isVisibleForKiosk = () => {
    if (toShowOnlyKiosks) {
      const showKioskIds = toShowOnlyKiosks.split(',').map((item) => item.trim());
      return showKioskIds.some((kioskId) => kioskId.toLowerCase() === currKioskId);
    }
    if (toHideKiosks) {
      const hideKioskIds = toHideKiosks.split(',').map((item) => item.trim());
      return !hideKioskIds.some((kioskId) => kioskId.toLowerCase() === currKioskId);
    }
    return true;
  };

  const isVisibleForVIP = () => {
    if (showToWip && isVip === '0') return false;
    return !(hideForWip && isVip === '1');
  };

  return isVisibleForKiosk() && isVisibleForVIP();
}

export function ecomGetCollectionOrProductVisibilityForWebapp(checkingObj, isVip) {
  const showToVip = checkingObj?.show_only_for_vip || checkingObj?.showOnlyForVip;
  const hideForVip = checkingObj?.hide_for_vip || checkingObj?.hideForVip;
  const visibilityJson = tryJson(checkingObj.visibility_json, {});

  let webappVisibility = checkingObj?.showOnWebapp;
  if (Object.prototype.hasOwnProperty.call(checkingObj || {}, 'show_on_webapp')) {
    webappVisibility = checkingObj.show_on_webapp;
  }
  if (Object.prototype.hasOwnProperty.call(visibilityJson, 'webapp')) {
    webappVisibility = visibilityJson.webapp;
  }

  if (isFalsy(webappVisibility)) {
    return false;
  }

  const isVisibleForVIP = () => {
    if (showToVip && isVip === '0') {
      return false;
    }
    return !(hideForVip && isVip === '1');
  };

  return isVisibleForVIP();
}

export function ecomGetCollectionOrProductVisibilityForChannel(checkingObj, isVip, channel) {
  if (channel === 'webapp') {
    return ecomGetCollectionOrProductVisibilityForWebapp(checkingObj, isVip);
  }
  if (channel === 'kiosk') {
    return ecomGetCollectionOrProductVisibility(checkingObj, isVip);
  }
  return false;
}

export function ecomGetAddonCollectionForWebapp(collectionList, isVip) {
  const addonCollection = collectionList?.find((collection) => collection?.is_addon_collection);
  return addonCollection && ecomGetCollectionOrProductVisibilityForWebapp(addonCollection, isVip)
    ? addonCollection
    : null;
}

export async function ecomGetCollectionsById({
  collectionId,
  eventPrivateApiKey,
  eventPublicApiKey,
  withProductData = true,
}) {
  return getCollectionsById({ id: collectionId, eventPrivateApiKey, eventPublicApiKey, withProductData });
}

export async function ecomGetCollectionsWithProductsByEventName({ eventPrivateApiKey, eventPublicApiKey, eventName }) {
  return getCollectionsWithProductsByEventName({ eventPrivateApiKey, eventPublicApiKey, eventName });
}

export async function ecomGetCollectionsByEventName(eventName) {
  return getCollectionsByEventName(eventName);
}

export async function ecomGetCurrentEventProducts({ withCombos = true, eventName }) {
  return getProductsByEventName({ withCombos, eventName });
}

export async function ecomGetShopLocationCodeList({ eventName }) {
  return getShopLocationCodeList({ eventName });
}

export async function ecomCreateProduct({ productData, eventName }) {
  return createProduct({ productData, eventName });
}

export async function ecomEditProduct({ productData, eventName }) {
  return editProduct({ productData, eventName });
}

export async function ecomDeleteProduct({ productData, eventName }) {
  return deleteProduct({ productData, eventName });
}

export function ecomFindAndSetFullUnlockProduct(internalCollection, externalCollection, isVip) {
  // Find the full unlock product in internal collection
  const fullUnlockProduct = internalCollection?.products?.find((product) => product?.product_purpose === 'full_unlock');

  if (!fullUnlockProduct) {
    return;
  }

  // Find the corresponding external product
  const externalProduct = externalCollection?.products.edges.find(
    (prod) => prod.node.id === normalizeProductId(fullUnlockProduct?.external_product_id)
  );

  if (!externalProduct) {
    return;
  }

  // Get the external variant ID
  const externalVariantId = ecomGetExternalProductVariantID(externalProduct, isVip);

  if (!externalVariantId) {
    return;
  }

  // Create and store the full unlock product with variant ID
  const newProduct = {
    ...fullUnlockProduct,
    variantId: externalVariantId,
  };

  localStorage.setItem('fullUnlockProduct', JSON.stringify(newProduct));
}

export async function ecomGetAddonCollectionVariantArray({
  collectionId,
  isVip,
  eventPrivateApiKey,
  eventPublicApiKey,
}) {
  const res = await getCollectionsById({ id: collectionId, eventPrivateApiKey, eventPublicApiKey });
  const { occoCollectionData, collectionAndComboData } = res?.status === 'ok' ? res.result : null;
  if (!occoCollectionData) {
    throw new Error(`No collection found for ID ${collectionId}`);
  }

  const variantArr = [];
  const products = orderProductsByCollectionJson(
    occoCollectionData.products,
    occoCollectionData.collection_product_json
  );
  const externalProducts = collectionAndComboData.collectionData.products.edges;

  for (let t = 0; t < products?.length; t += 1) {
    const variant = products[t];
    const resVariant = (() => {
      const product = externalProducts.find(({ node }) => node.id === normalizeProductId(variant.external_product_id));
      if (!product) return null;
      const variantEdges = product.node.variants.edges;
      const v = variantEdges.find(({ node }) => ecomIsNodeTitleHasCorrectMode(node, isVip)) ?? variantEdges[0];
      return v
        ? {
            variantId: v.node.id,
            price: v.node.price,
            sku: v.node.sku || variant.sku || null,
          }
        : null;
    })();

    if (resVariant) {
      variant.variantId = resVariant.variantId;
      variant.price = resVariant.price;
      variant.sku = resVariant.sku;
    }
    variantArr.push(variant);
  }
  return variantArr;
}

export function ecomGetExternalProductVariantID(externalProduct, isVip) {
  const variant = externalProduct.node.variants.edges.find((item) => ecomIsNodeTitleHasCorrectMode(item?.node, isVip));
  return variant ? variant.node.id : null;
}

export function ecomNormalizeProductVariantId(id) {
  return id?.includes(`gid://shopify`) ? id : `gid://shopify/ProductVariant/${id}`;
}

export function ecomGetCollectionTagType(collection) {
  const collectionTagType = {
    collectionAcceptTags: [],
    collectionDenyTags: [],
    comboAcceptTags: [],
    comboDenyTags: [],
  };
  const prodList = collection.products;
  for (let p = 0; p < prodList.length; p += 1) {
    const prodTags = ecomGetProductTagType(prodList[p]);
    if (prodTags.acceptTags.length > 0) {
      collectionTagType.comboAcceptTags = collectionTagType.comboAcceptTags.concat(prodTags.acceptTags);
    }
    if (prodTags.denyTags.length > 0) {
      collectionTagType.comboDenyTags = collectionTagType.comboDenyTags.concat(prodTags.denyTags);
    }
  }
  return collectionTagType;
}

export function ecomIsComboContainsRequiredOption(collection, option) {
  return collection.products.some((product) => product.product_purpose === option);
}

export function ecomProductCountForPhotoSelection(collection, option) {
  const { products } = collection;
  return products.reduce((count, product) => {
    if (product.product_purpose === option) {
      const prodCount = ecomGetProductCountInCollection(collection, product.id);
      return count + (prodCount || 1);
    }
    return count;
  }, 0);
}

export function ecomGetAddonCollectionId(collectionList) {
  for (let i = 0; i < collectionList.length; i += 1) {
    if (collectionList[i]?.is_addon_collection) {
      return collectionList[i].id;
    }
  }
  return -1;
}

export function ecomGetTipCollectionId(collectionList) {
  for (let i = 0; i < collectionList.length; i += 1) {
    if (isTipCollectionFlag(collectionList[i]?.is_tip_collection)) {
      return collectionList[i].id;
    }
  }
  return -1;
}

export function ecomGetTipCollectionForChannel(collectionList, isVip, channel) {
  const tipCollection = collectionList?.find((collection) => isTipCollectionFlag(collection?.is_tip_collection));
  return tipCollection && ecomGetCollectionOrProductVisibilityForChannel(tipCollection, isVip, channel)
    ? tipCollection
    : null;
}

/** Build tip-collection products for the pre-checkout tip popup (from loaded catalog data). */
export function ecomFindTipProducts(occoCollections, externalCollections, isVip, { channel } = {}) {
  const tipCollection = ecomGetTipCollectionForChannel(occoCollections, isVip, channel);
  if (!tipCollection) {
    return [];
  }

  const tipExternalId = normalizeCollectionId(tipCollection.external_collection_id);
  const externalMatch = (externalCollections || []).find((item) => {
    const collectionData = item?.collectionData || item;
    return normalizeCollectionId(collectionData?.id) === tipExternalId;
  });
  // Prefer tip-collection edges; fall back to any collection that contains the product.
  const tipEdges = (externalMatch?.collectionData || externalMatch)?.products?.edges || [];
  const allEdges = (externalCollections || []).flatMap((item) => {
    const collectionData = item?.collectionData || item;
    return collectionData?.products?.edges || [];
  });
  const currency = tipCollection.currency || 'USD';

  return (tipCollection.products || [])
    .filter((product) => ecomGetCollectionOrProductVisibilityForChannel(product, isVip, channel))
    .reduce((tipProducts, product) => {
      const productId = normalizeProductId(product?.external_product_id);
      const externalProduct =
        tipEdges.find((p) => p?.node?.id === productId) || allEdges.find((p) => p?.node?.id === productId);
      const variantId =
        (externalProduct ? ecomGetExternalProductVariantID(externalProduct, isVip) : null) ||
        externalProduct?.node?.variants?.edges?.[0]?.node?.id ||
        null;
      if (!variantId) {
        return tipProducts;
      }

      const amount = Number(isVip === '1' || isVip === true ? product.vip_price : product.non_vip_price) || 0;
      tipProducts.push({
        id: product.id,
        title: product.name,
        variantId,
        amount,
        formattedPrice: ecomFormatPrice(amount, product.currency || currency),
        imageUrl: ecomGetLocalisedObjectImage(product) || '',
      });
      return tipProducts;
    }, [])
    .sort((a, b) => a.amount - b.amount);
}

/** Load tip products from the event catalog (same source as packages/collections). */
export async function ecomLoadTipProducts({ channel } = {}) {
  const eventName = localStorage.getItem('eventName') || '';
  if (!eventName) {
    return [];
  }

  try {
    const res = await ecomGetCollectionsWithProductsByEventName({
      eventPrivateApiKey: localStorage.getItem('eventPrivateApiKey') || '',
      eventPublicApiKey: localStorage.getItem('eventPublicApiKey') || '',
      eventName,
    });
    if (res?.status !== 'ok') {
      return [];
    }
    return ecomFindTipProducts(
      res.result?.occoCollectionData || [],
      res.result?.collectionData || [],
      localStorage.getItem('isVip'),
      { channel }
    );
  } catch (err) {
    console.error('Failed to load tip products:', err);
    return [];
  }
}

export function ecomHasTipProduct(checkout) {
  for (const lineItem of checkout?.lineItems || []) {
    const variantArr = tryJson(ecomGetCustomAttribute(lineItem, '_variantArr'), []);
    if (Array.isArray(variantArr) && variantArr.some((v) => (v?.productPurpose || v?.product_purpose) === 'tip')) {
      return true;
    }
  }
  return false;
}

export function ecomBuildTipLineItem(tipProduct, { eventName, attendeeUid, kioskId, uniqueField }) {
  const variant = {
    productTitle: tipProduct.title,
    pid: 0,
    eventName,
    id: tipProduct.id,
    attendeeUid,
    action: 'tip',
    productPurpose: 'tip',
    uniqueField,
  };
  if (kioskId) {
    variant.kioskId = kioskId;
  }
  return {
    variantId: tipProduct.variantId,
    quantity: 1,
    customAttributes: [{ key: '_variantArr', value: JSON.stringify([variant]) }],
  };
}

export function ecomRenderTipCheckoutPopupHtml({ tipProducts, message, description, noTipLabel }) {
  const buttons = (tipProducts || [])
    .map((tip) => {
      const tipId = escapeHtml(tip.id);
      const price = escapeHtml(tip.formattedPrice || '');
      const title = escapeHtml(tip.title || 'Tip');
      const imageUrl = tip.imageUrl ? escapeHtml(tip.imageUrl) : '';
      const imageHtml = imageUrl
        ? `<img class="tip-amount-btn__img" src="${imageUrl}" alt="${title}" />`
        : '';
      return `<button type="button" class="tip-amount-btn" data-tip-id="${tipId}">
          ${imageHtml}
          <span class="tip-amount-btn__price">${price}</span>
        </button>`;
    })
    .join('');
  const descriptionHtml = description
    ? `<p class="tip-checkout-popup__description">${escapeHtml(description)}</p>`
    : '';
  return `<div class="confirm-purchase-popup tip-checkout-popup">
    <h2 class="tip-checkout-popup__title">${escapeHtml(message)}</h2>
    ${descriptionHtml}
    <div class="tip-checkout-popup__amounts">${buttons}</div>
    <button id="noTipBtn" type="button" class="btn btn-outline tip-checkout-popup__skip" data-dismiss="modal">${escapeHtml(noTipLabel)}</button>
  </div>`;
}

export function ecomIsRequiredImageSelectionForProduct(product) {
  const requiredImageSelection = product?.productPurpose || product?.product_purpose;
  return requiredImageSelection === 'printable' || requiredImageSelection === 'partial_unlock_media';
}

export function ecomHasFullUnlockProduct(checkout) {
  let hasFullUnlock = false;
  const lineItems = checkout.lineItems || [];
  for (let k = 0; k < lineItems.length; k += 1) {
    const lineItem = lineItems[k];
    const variantArr = JSON.parse(ecomGetCustomAttribute(lineItem, '_variantArr'));
    for (let i = 0; i < variantArr.length; i += 1) {
      if (variantArr[i].productPurpose === 'full_unlock') {
        hasFullUnlock = true;
        break;
      }
    }
  }
  return hasFullUnlock;
}

function ecomGetCustomAttribute(lineItem, customAttName) {
  const lineItemArray = Array.isArray(lineItem) ? lineItem : [lineItem];
  for (let i = 0; i < lineItemArray.length; i += 1) {
    const item = lineItemArray[i];
    if (item.customAttributes && Array.isArray(item.customAttributes)) {
      for (let j = 0; j < item.customAttributes.length; j += 1) {
        const custAtt = item.customAttributes[j];
        if (custAtt.key === customAttName) {
          return custAtt.value;
        }
      }
    }
  }
  return null;
}

export function ecomAlertDigitalPackDuplicationAndRedirect(variant, cart) {
  if (variant.product_purpose === 'full_unlock' && ecomHasFullUnlockProduct(cart)) {
    const lsMessage = localStorage.getItem('shopDigitalProductDupMsg');
    const message = lsMessage || t('checkout-digital-package-duplicate-message');
    const content = `<div>
        <h6>${message} </h6><br> <br>
        <button id="buttonOK" type="button" class="btn btn-primary btn-lg"> OK </button></div>`;
    customPopUp.html(content);
    customPopUp.show();
    document.getElementById('buttonOK')?.addEventListener(
      'click',
      () => {
        window.location.href = 'cart.html';
      },
      { once: true }
    );
    return true;
  }
  return false;
}

export function ecomIsValidForPrint(photoFullPath, tags) {
  const fileExtension = photoFullPath.split('.').pop().toLowerCase();
  return !!(
    /(jpg|jpeg|png)$/gi.test(fileExtension) ||
    (photoFullPath.includes('uokg5o') && tags.acceptTags.includes('uokg5o'))
  );
}

export function ecomApplyDiscount({ cartId, discountCode, eventPrivateApiKey, eventPublicApiKey }) {
  return cartApplyDiscount({ cartId, discountCode, eventPrivateApiKey, eventPublicApiKey });
}

export function ecomGetCart({ isFromWebapp, shippingAddress, eventPrivateApiKey, eventPublicApiKey }) {
  return cartGetCart({ isFromWebapp, shippingAddress, eventPrivateApiKey, eventPublicApiKey });
}

export function ecomAddLineItems(cartId, lineItems) {
  return cartAddLineItems(cartId, lineItems);
}

export function ecomUpdateCartLineItems({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey }) {
  return cartUpdateCartLineItems({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey });
}

export function normalizeProductId(id) {
  return normalizeProductIdInternal(id);
}

export function normalizeCollectionId(id) {
  return normalizeCollectionIdInternal(id);
}

export function ecomIsValidForPrintAndUnlock(photoFullPath, tags) {
  const fileExtension = photoFullPath.split('.').pop().toLowerCase();
  return !!(
    /(jpg|jpeg|png)$/gi.test(fileExtension) ||
    localStorage.getItem('isUnlockMedia') === 'true' ||
    (photoFullPath.includes('uokg5o') && tags.acceptTags.includes('uokg5o'))
  );
}

function isAddonCollection(collection) {
  const flag = collection?.is_addon_collection;
  return flag === true || flag === 1 || flag === '1';
}

function addonProductMatchesAlbum(product, imgPaths) {
  return ecomCheckProductTagTypeForAllImages(imgPaths, ecomGetProductTagType(product));
}

function comboProductMatchesAlbum(product, imgPaths) {
  const { acceptTags } = ecomGetProductTagType(product);
  return ecomCheckProductTagTypeForAllImages(imgPaths, { acceptTags });
}

export function ecomCheckCollectionTagsAndImages(collection, imgPaths) {
  const products = collection?.products ?? [];
  if (isAddonCollection(collection)) {
    return products.length === 0 || products.some((product) => addonProductMatchesAlbum(product, imgPaths));
  }
  return products.every((product) => comboProductMatchesAlbum(product, imgPaths));
}

export const PRODUCT_OPTION_TYPE = {
  NONE: 0,
  PRINTABLE: 1,
  PARTIAL_UNLOCK_MEDIA: 2,
  PRINTABLE_AND_PARTIAL_UNLOCK_MEDIA: 3,
};

export function ecomGetProductOptionType(collection) {
  const { products } = collection;

  if (!Array.isArray(products) || products.length === 0) {
    return PRODUCT_OPTION_TYPE.NONE;
  }

  let hasPrintable = false;
  let hasPartialUnlock = false;

  for (const product of products) {
    const purpose = product?.product_purpose || product?.productPurpose;
    if (purpose === 'printable') {
      hasPrintable = true;
    } else if (purpose === 'partial_unlock_media') {
      hasPartialUnlock = true;
    }
    if (hasPrintable && hasPartialUnlock) {
      return PRODUCT_OPTION_TYPE.PRINTABLE_AND_PARTIAL_UNLOCK_MEDIA;
    }
  }

  if (hasPrintable) return PRODUCT_OPTION_TYPE.PRINTABLE;
  if (hasPartialUnlock) return PRODUCT_OPTION_TYPE.PARTIAL_UNLOCK_MEDIA;
  return PRODUCT_OPTION_TYPE.NONE;
}

export function ecomFilterCollectionData(collectionFullData) {
  const { occoCollectionData } = collectionFullData;
  const newCollection = occoCollectionData;
  const newExternalCollection = collectionFullData.collectionAndComboData.collectionData;
  const isVip = localStorage.getItem('isVip');
  const filteredOccoProducts = [];
  const filteredExternalProducts = [];
  if ((occoCollectionData?.show_only_for_vip && isVip === '0') || (occoCollectionData?.hide_for_vip && isVip === '1')) {
    return null;
  }
  for (let i = 0; i < occoCollectionData.products.length; i += 1) {
    const product = newCollection.products[i];
    const isVisibleToUser = !(
      (product?.show_only_for_vip && isVip === '0') ||
      (product?.hide_for_vip && isVip === '1')
    );
    if (isVisibleToUser) {
      filteredOccoProducts.push(product);
      const { edges } = newExternalCollection.products;
      filteredExternalProducts.push(getExternalProductsFromCollection(product.external_product_id, edges));
    }
  }
  if (filteredOccoProducts.length > 0) {
    newCollection.products = filteredOccoProducts;
    newExternalCollection.products.edges = filteredExternalProducts;
  } else {
    return null;
  }

  return {
    occoCollectionData: newCollection,
    collectionAndComboData: {
      collectionComboData: collectionFullData.collectionAndComboData.collectionComboData,
      collectionData: newExternalCollection,
    },
  };
}

function getExternalProductsFromCollection(externalProductId, externalProductList) {
  const externalId = normalizeProductIdInternal(externalProductId);
  for (let i = 0; i < externalProductList.length; i += 1) {
    if (externalProductList[i].node.id === externalId) return externalProductList[i];
  }
  return null;
}

export async function ecomCreateCollection({ collectionData, eventName }) {
  return createCollection({ collectionData, eventName });
}

export async function ecomEditCollection({ collectionData, eventName }) {
  return editCollection({ collectionData, eventName });
}

export async function ecomReorderCollections({ eventName, orderedIds }) {
  return reorderCollections({ eventName, orderedIds });
}

export async function ecomDeleteCollection({ collectionData, eventName }) {
  return deleteCollection({ collectionData, eventName });
}

export async function ecomAddStoreSettings({ settingsData, eventName }) {
  return addStoreSettings({ settingsData, eventName });
}

export async function ecomEditStoreSettings({ settingsData, eventName }) {
  return editStoreSettings({ settingsData, eventName });
}

export async function ecomGetStoreSettings({ eventName, eventCode }) {
  return getStoreSettings({ eventName, eventCode });
}

export async function ecomDeleteStoreSettings({ settingsData, eventName }) {
  return deleteStoreSettings({ settingsData, eventName });
}

export async function ecomAddStoreLocation({ locationData, eventName }) {
  return addStoreLocation({ locationData, eventName });
}

export async function ecomEditStoreLocation({ locationData, eventName }) {
  return editStoreLocation({ locationData, eventName });
}

export async function ecomGetStoreLocation({ eventName, eventCode }) {
  return getStoreLocation({ eventName, eventCode });
}

export async function ecomGetStoreSettingsForKiosk({ eventName, eventPublicApiKey }) {
  return getStoreSettingsForKiosk({ eventName, eventPublicApiKey });
}

export async function ecomGetStoreLocationForKiosk({ eventName, eventPublicApiKey }) {
  return getStoreLocationForKiosk({ eventName, eventPublicApiKey });
}

export {
  loadEcommerceCheckoutSettings,
  applyEcommerceCheckoutSettingsToStorage,
  extractEcomApiRecord,
  mapLocationToLegacyShippingAddress,
  isTruthyEcomFlag,
  toEcomStorageOneOrZero,
  parseLegacyShippingAddress,
};

export async function ecomDeleteStoreLocation({ locationData, eventName }) {
  return deleteStoreLocation({ locationData, eventName });
}

export async function ecomPrintOrderPhotos({ orderId, eventPrivateApiKey, eventName }) {
  const timeBasedKey = getTimeBasedKey();
  return printOrderPhotos({ orderId, timeBasedKey, eventPrivateApiKey, eventName });
}

export async function ecomGetEventOrders({ startDate, endDate, eventName }) {
  const result = await getEventOrders({ startDate, endDate, eventName });
  if (result?.status === 'ok' && Array.isArray(result.result)) {
    const { orders, skippedCount } = normalizeEventOrders(result.result);
    return {
      ...result,
      result: orders,
      skippedOrderCount: skippedCount,
    };
  }
  return result;
}

export async function ecomFulfillOrder({ orderId, eventName }) {
  return fulfillOrder({ orderId, eventName });
}

export async function ecomCreateDraftOrder({ attendeeUid, cartId, eventPrivateApiKey }) {
  return createDraftOrder({ attendeeUid, cartId, eventPrivateApiKey });
}

export async function ecomGetAttendeeLastOrder({ eventPublicApiKey, attendeeId }) {
  // Public last-order is already an allowlisted DTO (no id). Do not run it through
  // normalizeEventOrder — that helper is for the owner list and drops payloads without id.
  return getAttendeeLastOrder({ eventPublicApiKey, attendeeId });
}

export async function ecomCompleteDraftOrder({
  attendeeUid,
  draftOrderId,
  employeeSecretCode,
  authRequired,
  isByCash,
  eventPrivateApiKey,
}) {
  return completeDraftOrder({
    attendeeUid,
    draftOrderId,
    employeeSecretCode,
    authRequired,
    isByCash,
    eventPrivateApiKey,
  });
}

export async function ecomCompleteShopifyDraftOrderSelfCheckout({
  attendeeUid,
  draftOrderId,
  gateway,
  tag,
  eventPrivateApiKey,
}) {
  return completeShopifyDraftOrderSelfCheckout({
    attendeeUid,
    draftOrderId,
    gateway,
    tag,
    eventPrivateApiKey,
  });
}

/**
 * Generate a time-based key that expires every 2 minutes
 * Returns the same key within a 2-minute window, generates a new one after expiration
 * @returns {string} - Time-based key
 */
const timeBasedKeyCache = {
  key: null,
  timestamp: null,
  expirationTime: 30 * 1000, // 30 seconds in milliseconds
};

export function getTimeBasedKey() {
  const now = Date.now();

  // Check if we have a valid cached key
  if (
    timeBasedKeyCache.key !== null &&
    timeBasedKeyCache.timestamp !== null &&
    now - timeBasedKeyCache.timestamp < timeBasedKeyCache.expirationTime
  ) {
    // Return cached key if still valid
    return timeBasedKeyCache.key;
  }

  // Generate new key if expired or not set
  const newKey = generateRandomKey();
  timeBasedKeyCache.key = newKey;
  timeBasedKeyCache.timestamp = now;

  return newKey;
}

/**
 * Generate a random key string
 * @returns {string} - Random key
 */
function generateRandomKey() {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  return `${timestamp}_${randomStr}`;
}

export function ecomGetProductAcceptDenyTags(product) {
  const isUnlockMedia =
    product.product_purpose === 'partial_unlock_media' || product.productPurpose === 'partial_unlock_media';
  const requireTags = product.require_tags || product.requireTags || '';
  const denyTagsStr = product.deny_tags || product.denyTags || '';
  const acceptTags = requireTags?.length > 0 ? requireTags.split(',').map((tag) => tag.trim()) : [];
  const denyTags = denyTagsStr?.length > 0 ? denyTagsStr.split(',').map((tag) => tag.trim()) : [];
  return {
    acceptTags,
    denyTags,
    isUnlockMedia,
  };
}

export {
  ecomGetComboProductsIds,
  ecomGetCollComboVariant,
  ecomGetCollectionPackageVariantID,
  ecomGetProductsFromCollection,
  ecomGetProductCountInCollection,
  groupVariantsByIdInOrder,
  ecomInternalProductToVariant,
  ecomExtractPageLang,
  ecomGetLocalisedObjectImage,
  ecomFormatCurrency,
  ecomFormatDecimals,
  ecomFormatPrice,
  ecomGetProductTagType,
  ecomCheckProductTagTypeAndImageTag,
  ecomCheckProductTagTypeForAllImages,
  fromUrlFilename,
  normalizeEventOrder,
  normalizeEventOrders,
};
